import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

export interface NavItem {
  title: string;
  path?: string;
  externalUrl?: string;
  queryParams?: { [key: string]: string };
  icon?: string;
  svgIcon?: string;
  isNew?: boolean;
  disabled?: boolean;
  allowedRoles?: string[];
  children?: NavItem[];
}

export interface NavSection {
  title?: string;
  children: NavItem[];
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private sections: NavSection[] = [];
  private accountSections: NavSection[] = [];

  constructor(private translate: TranslateService) {
    this.buildSections();
    this.buildAccountSections();
  }

  private t(key: string) {
    return key;
  }

  private buildSections() {
    this.sections = [
      {
        children: [
          {
            title: this.t('homeTitle'),
            path: 'home',
            icon: 'nav-home.svg',
            isNew: false,
            allowedRoles: ['centeradmin', 'groupadmin', 'user'],
          },
          {
            title: this.t('controlPanelTitle'),
            path: 'admin-content-management',
            icon: 'nav-settings.svg',
            isNew: false,
            allowedRoles: ['groupadmin'],
          },
        ],
      },
      {
        title: this.t('navbar.asistente'),
        children: [
          {
            title: this.t('chatAi.navTitle'),
            path: 'chat-ai',
            icon: 'nav-chat.svg',
            isNew: true,
            allowedRoles: ['centeradmin', 'groupadmin', 'centeruser', 'superadmin'],
          },
        ],
      },
      {
        title: this.t('navbar.gestionOperaciones'),
        children: [
          {
            title: this.t('navbar.agenda'),
            path: 'calendar',
            icon: 'nav-calendar.svg',
            allowedRoles: ['centeradmin', 'groupadmin', 'centeruser'],
            children: [
              {
                title: this.t('navbar.addService'),
                path: 'calendar',
                queryParams: { openModal: 'true' },
                icon: 'add.svg',
                allowedRoles: ['centeradmin', 'groupadmin', 'centeruser'],
              },
            ],
          },
          {
            title: this.t('navbar.budgets'),
            icon: 'nav-budget.svg',
            allowedRoles: ['centeradmin'],
            children: [
              {
                title: this.t('navbar.allBudgets'),
                icon: 'nav-all-budget.svg',
                path: 'budget-list',
                allowedRoles: ['centeradmin'],
              },
              {
                title: this.t('navbar.createStandardBudget'),
                icon: 'nav-new-budget.svg',
                path: 'budget/new',
                allowedRoles: ['centeradmin'],
                queryParams: { fresh: 'true' },
              },
              {
                title: this.t('navbar.createAdvancedBudget'),
                icon: 'nav-budget.svg',
                disabled: true,
                allowedRoles: ['centeradmin'],
              },
              {
                title: this.t('navbar.budgetDashboard'),
                icon: 'nav-budget-dashboard.svg',
                disabled: true,
                allowedRoles: ['centeradmin'],
              },
            ],
          },
          {
            title: this.t('navbar.myWorkshop'),
            icon: 'nav-my-workshop.svg',
            allowedRoles: ['centeradmin', 'groupadmin'],
            children: [
              {
                title: this.t('navbar.employeeManagement'),
                path: 'employees',
                icon: 'nav-employee.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
                children: [
                  {
                    title: this.t('navbar.addEmployee'),
                    path: 'employees',
                    queryParams: { openModal: 'true' },
                    icon: 'add-employee.svg',
                    allowedRoles: ['centeradmin', 'groupadmin'],
                  },
                ],
              },
              {
                title: this.t('navbar.services'),
                path: 'services',
                icon: 'nav-services.svg',
                allowedRoles: ['centeradmin', 'groupadmin', 'centeruser'],
                children: [
                  {
                    title: this.t('navbar.addService'),
                    path: 'services',
                    queryParams: { openModal: 'true' },
                    icon: 'add.svg',
                    allowedRoles: ['centeradmin', 'groupadmin', 'centeruser'],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        title: this.t('navbar.inventarioCompras'),
        children: [
          {
            title: this.t('navbar.miMarketplace'),
            icon: 'nav-marketplace.svg',
            allowedRoles: ['centeradmin'],
            children: [
              {
                title: this.t('navbar.tyres'),
                path: 'marketplace',
                icon: 'nav-tyres.svg',
                allowedRoles: ['centeradmin'],
              },
            ],
          },
          {
            title: this.t('navbar.stock'),
            icon: 'nav-inventory.svg',
            allowedRoles: ['centeradmin', 'groupadmin'],
            children: [
              {
                title: this.t('navbar.addProducts'),
                path: 'stock',
                queryParams: { openModal: 'true' },
                icon: 'add.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
              {
                title: this.t('navbar.stockControl'),
                path: 'stock',
                icon: 'nav-stock-control.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
              {
                title: this.t('navbar.alertSettings'),
                path: 'stock/alerts',
                icon: 'nav-alerts.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
            ],
          },
          {
            title: this.t('navbar.pedidos'),
            icon: 'nav-orders.svg',
            allowedRoles: ['centeradmin', 'groupadmin'],
            children: [
              {
                title: this.t('navbar.orderReception'),
                path: 'orders',
                queryParams: { tab: 'reception' },
                icon: 'nav-list.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
              {
                title: this.t('navbar.history'),
                path: 'orders',
                queryParams: { tab: 'history' },
                icon: 'clock.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
            ],
          },
          {
            title: this.t('navbar.priceManagement'),
            icon: 'nav-prices.svg',
            allowedRoles: ['centeradmin', 'groupadmin'],
            children: [
              {
                title: this.t('navbar.configuredRules'),
                path: 'prices',
                icon: 'nav-calculator.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
              {
                title: this.t('navbar.addRule'),
                path: 'prices',
                queryParams: { action: 'addRule' },
                icon: 'add.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
            ],
          },
          {
            title: this.t('navbar.suppliers'),
            icon: 'nav-providers.svg',
            allowedRoles: ['centeradmin', 'groupadmin'],
            children: [
              {
                title: this.t('navbar.allSuppliers'),
                path: 'suppliers',
                icon: 'nav-list.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
              {
                title: this.t('navbar.addSupplier'),
                path: 'suppliers',
                queryParams: { action: 'addSupplier' },
                icon: 'add.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
              {
                title: this.t('navbar.addStock'),
                path: 'suppliers',
                queryParams: { action: 'addStock' },
                icon: 'nav-add-stock.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
            ],
          },
        ],
      },
      {
        title: this.t('navbar.clientesVehiculos'),
        children: [
          {
            title: this.t('navbar.clients'),
            icon: 'user-icon.svg',
            path: 'clients',
            allowedRoles: ['centeradmin'],
            children: [
              {
                title: this.t('navbar.addClient'),
                path: 'clients',
                queryParams: { openModal: 'true' },
                icon: 'add-employee.svg',
                allowedRoles: ['centeradmin'],
              },
            ],
          },
          {
            title: this.t('navbar.vehicles'),
            icon: 'nav-vehicles.svg',
            path: 'vehicles',
            allowedRoles: ['centeradmin'],
            children: [
              {
                title: this.t('navbar.addVehicle'),
                path: 'vehicles',
                queryParams: { openModal: 'true' },
                icon: 'nav-add-circle.svg',
                allowedRoles: ['centeradmin'],
              },
            ],
          },
          {
            title: this.t('navbar.fleetManagement'),
            icon: 'nav-fleet.svg',
            path: 'fleets',
            allowedRoles: ['centeradmin', 'groupadmin'],
            children: [
              {
                title: this.t('navbar.myTasks'),
                path: 'my-tasks',
                icon: 'nav-my-tasks.svg',
                allowedRoles: ['centeradmin', 'groupadmin'],
              },
            ],
          },
        ],
      },
      {
        title: this.t('notificationsSectionTitle'),
        children: [
          {
            title: this.t('notificationCenterTitle'),
            path: 'notification-center',
            icon: 'nav-alerts.svg',
            isNew: true,
            allowedRoles: ['groupadmin', 'centeradmin'],
          },
          {
            title: this.t('notificationSettingsTitle'),
            path: 'notification-settings',
            icon: 'navbar-settings.svg',
            isNew: true,
            allowedRoles: ['groupadmin', 'centeradmin'],
          },
        ],
      },
      {
        title: this.t('navbar.ecommerce'),
        children: [
          {
            title: this.t('navbar.orderManagement'),
            path: 'orders-panel',
            icon: 'nav-manage-orders.svg',
            isNew: false,
            allowedRoles: ['centeradmin', 'groupadmin'],
          },
        ],
      },
      ...(environment.featureFlags?.fintech ? [{
        title: this.t('navbar.facturacion'),
        children: [
          {
            title: this.t('fintechTitle'),
            externalUrl: 'https://app.alegra.com/',
            icon: 'nav-budget.svg',
            allowedRoles: ['centeradmin', 'groupadmin'],
          },
        ],
      }] : []),
      {
        title: this.t('navbar.analysis'),
        children: [
          {
            title: this.t('navbar.reports'),
            icon: 'nav-reports.svg',
            allowedRoles: ['centeradmin', 'groupadmin', 'user'],
            children: [
              {
                title: this.t('navbar.inventoryReport'),
                path: 'home-reports',
                queryParams: { tab: 'inventory' },
                icon: 'nav-inventory.svg',
                allowedRoles: ['centeradmin', 'groupadmin', 'user'],
              },
              {
                title: this.t('navbar.workshopReport'),
                path: 'home-reports',
                queryParams: { tab: 'workshop' },
                icon: 'nav-report-management.svg',
                allowedRoles: ['centeradmin', 'groupadmin', 'user'],
              },
              {
                title: this.t('navbar.rappel'),
                path: 'home-reports',
                queryParams: { tab: 'rappel' },
                icon: 'nav-rappel.svg',
                allowedRoles: ['centeradmin', 'groupadmin', 'user'],
              },
              {
                title: this.t('navbar.bonusReport'),
                path: 'home-reports',
                queryParams: { tab: 'bonus' },
                icon: 'nav-rappel.svg',
                allowedRoles: ['groupadmin'],
              },
            ],
          },
        ],
      },
    ];
  }

  private buildAccountSections() {
    this.accountSections = [
      {
        title: '',
        children: [
          {
            title: this.t('contactSupport'),
            path: '/',
            icon: 'support.svg',
          },
        ],
      },
    ];
  }

  getSections(): NavSection[] {
    return this.sections;
  }

  getAccountSections(): NavSection[] {
    return this.accountSections;
  }
}
